import { Button, Input, message, Popover } from "antd";
import React, { useState } from "react";
import { IoIosCloudDownload } from "react-icons/io";
import ReportTable from "../Report/ReportTable";
import TotalTable from "../Report/TotalTable";
import ReportDiagnosis from "../Report/ReportDiagnosis";
import {
  decryptId,
  downloadBase64FileToDocx,
  downloadBase64Pdf,
} from "@/utils/utilsMethods";
import { FaFileWord, FaRegFilePdf } from "react-icons/fa";
import { diagnosisService } from "@/stores/services/diagnosisServices";
import { useSearchParams } from "react-router-dom";
import FloatLabel from "@/Components/FloatLabel";
import { MdArrowBackIosNew } from "react-icons/md";
import { useAppDispatch } from "@/stores/hooks";
import { setPrevDiagnosisCurrent } from "@/stores/features/utilsSlice";
import DownLoadWorkPlan from "./DownLoadWorkPlan";
import { FiSend } from "react-icons/fi";
import { skipToken } from "@reduxjs/toolkit/query";

interface DownLoadReportProps {
  companyId: number;
  secuence: string;
  schedule: string;
}

export const DownloadContent = ({
  companyId,
  diagnosisId,
}: {
  companyId: number;
  diagnosisId: number;
}) => {
  const [generateReport, { isLoading }] =
    diagnosisService.useGenerateReportMutation();

  const [sendReport, { isLoading: emailLoading }] =
    diagnosisService.useSendReportMutation();

  const { data: diagnosisDataById } = diagnosisService.useFindByIdQuery(
    diagnosisId ? { diagnosisId } : skipToken,
  );

  const [emailTo, setEmailTo] = useState<string>("");

  const handleSendReport = async () => {
    try {
      await sendReport({
        email_to: emailTo,
        diagnosis: diagnosisId ?? 0,
        company: companyId,
        schedule: parseInt(diagnosisDataById?.diagnosis.schedule || "0"),
        sequence: parseInt(diagnosisDataById?.diagnosis.sequence || "0"),
      }).unwrap();
      message.success("Correo enviado correctamente");
    } catch (error: any) {
      message.error(
        error.data?.message ||
          error.data?.error ||
          "Ocurrió un error al enviar el correo",
      );
    }
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center gap-4 ">
        <div className="w-full flex justify-around flex-1 gap-2">
          <Button
            loading={isLoading}
            onClick={async () => {
              const generateFile = await generateReport({
                format_to_save: "word",
                diagnosisId: diagnosisId ?? 0,
              }).unwrap();

              downloadBase64FileToDocx(
                generateFile.file,
                `Diagnostico_PESV_${new Date().toLocaleDateString("es-CO")}.docx`,
              );
            }}
            icon={<FaFileWord />}
            type="primary"
            htmlType="button"
            className="w-1/2"
          ></Button>
          <Button
            loading={isLoading}
            icon={<FaRegFilePdf />}
            className="bg-red-500 text-white border-red-500 active:bg-red-700 hover:bg-red-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 w-1/2"
            htmlType="button"
            onClick={async () => {
              const generateFile = await generateReport({
                format_to_save: "pdf",
                diagnosisId: diagnosisId ?? 0,
              }).unwrap();

              downloadBase64Pdf(
                generateFile.file,
                `Diagnostico_PESV_${new Date().toLocaleDateString("es-CO")}.pdf`,
              );
            }}
          ></Button>
        </div>
        <div className="w-full flex flex-col">
          <Popover
            content={
              <>
                <div className="w-full flex flex-col justify-center items-start px-4 gap-2 gap-y-2">
                  <div className="mt-2">
                    <FloatLabel label="Enviar email a">
                      <Input
                        id="email_to"
                        name="email_to"
                        type="email"
                        value={emailTo}
                        onChange={(e) => setEmailTo(e.target.value)}
                      />
                    </FloatLabel>
                  </div>
                  <Button
                    icon={<FiSend />}
                    onClick={handleSendReport}
                    className="w-full"
                    loading={emailLoading}
                  >
                    Enviar
                  </Button>
                </div>
              </>
            }
            title="Envio de correo"
            trigger="click"
            placement="left"
          >
            <Button
              loading={isLoading}
              icon={<FaRegFilePdf />}
              className="bg-purple-500 text-white border-purple-500 active:bg-purple-700 hover:bg-purple-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300"
              htmlType="button"
            >
              Enviar por email
            </Button>
          </Popover>
        </div>
      </div>
    </>
  );
};

export default function DownLoadReport({ companyId }: DownLoadReportProps) {
  const [searchParams] = useSearchParams();
  const diagnosisParam = searchParams.get("diagnosis");
  const diagnosisId = diagnosisParam
    ? parseInt(decryptId(diagnosisParam))
    : undefined;
  const dispatch = useAppDispatch();

  const handleBackStep = () => dispatch(setPrevDiagnosisCurrent());
  return (
    <div className="flex flex-col flex-1 w-full md:w-2/4">
      <div className="flex w-full justify-between">
        <Button
          className="w-full md:w-auto"
          icon={<MdArrowBackIosNew />}
          onClick={handleBackStep}
        >
          Atras
        </Button>
        <div className="flex flex-col md:flex-row items-center justify-start md:justify-evenly gap-2 w-full md:w-auto">
          <Popover
            content={
              <DownloadContent
                companyId={companyId}
                diagnosisId={diagnosisId ?? 0}
              />
            }
            trigger="click"
            placement="bottom"
          >
            <Button
              className="w-full text-orange-400 border-orange-400 active:bg-orange-700 hover:bg-orange-100"
              icon={<IoIosCloudDownload />}
              type="dashed"
            >
              Descargar Informe
            </Button>
          </Popover>
          <DownLoadWorkPlan companyId={companyId} />
        </div>
      </div>
      <div className="flex flex-col md:flex-row w-full flex-1 justify-between items-start gap-4 ">
        <div className="w-full md:w-2/4 ">
          <ReportTable companyid={companyId} />
        </div>
        <div className="flex w-full md:w-2/4 h-full flex-col items-center sticky top-2 gap-2 shadow-[rgba(7,_65,_210,_0.1)_0px_9px_30px] m-2 py-4 rounded-lg bg-gradient-to-br from-slate-50 to-neutral-100">
          <TotalTable companyId={companyId} />
          <ReportDiagnosis companyId={companyId} />
        </div>
      </div>
    </div>
  );
}
